import type { SpaceOption } from "@/app/_store/types/dtos";
import { createSlice } from "@reduxjs/toolkit";
import { loadAllSpaces, preloadSpacesBySize } from "./spaces.thunks";
import type { RootState } from "../store";

type SpacesState = {
  byId: Record<number, SpaceOption>;
  listAll: number[];
  bySize: Record<
    number,
    {
      ids: number[];
      status: "idle" | "loading" | "ready" | "error";
      error?: string;
    }
  >; // key = sizeId
};

const initialState: SpacesState = {
  byId: {},
  listAll: [],
  bySize: {},
};

export { loadAllSpaces, preloadSpacesBySize };

const spacesSlice = createSlice({
  name: "spaces",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(preloadSpacesBySize.pending, (state, action) => {
        const k = Number(action.meta.arg);
        const prev = state.bySize[k];
        state.bySize[k] = {
          ids: prev?.ids ?? [],
          status: "loading",
          error: undefined,
        };
      })
      .addCase(preloadSpacesBySize.fulfilled, (state, action) => {
        const { sizeId, spaces } = action.payload;
        for (const s of spaces) {
          state.byId[s.id] = { id: s.id, label: s.label } as SpaceOption;
          if (!state.listAll.includes(s.id)) state.listAll.push(s.id);
        }
        state.bySize[sizeId] = {
          ids: spaces.map((s) => s.id),
          status: "ready",
        } as any;
      })
      .addCase(preloadSpacesBySize.rejected, (state, action) => {
        const k = Number(action.meta.arg);
        const prev = state.bySize[k];
        state.bySize[k] = {
          ids: prev?.ids ?? [],
          status: "error",
          error: action.error?.message,
        };
      })
      .addCase(loadAllSpaces.pending, (_state) => {
        // optionally clear listAll or do nothing
        // state.listAll = [];
      })
      .addCase(loadAllSpaces.fulfilled, (state, action) => {
        const incomingIds: number[] = [];
        for (const s of action.payload) {
          state.byId[s.id] = {
            id: s.id,
            label: s.label,
            maxOverall: s.maxOverall,
          } as SpaceOption;
          incomingIds.push(s.id);
        }
        // Replace listAll to reflect current filter (e.g., species)
        state.listAll = incomingIds;
      })
      .addCase(loadAllSpaces.rejected, (_state, _action) => {
        // optionally log error or ignore
      });
  },
});

export const spacesReducer = spacesSlice.reducer;

// Selectors
export const selectSpacesForSize = (
  state: RootState,
  sizeId?: number | null
) => {
  if (sizeId == null || isNaN(sizeId)) return [] as SpaceOption[];
  const entry = state.spaces.bySize[sizeId];
  if (!entry || !Array.isArray(entry.ids)) return [] as SpaceOption[];
  return entry.ids
    .map((id) => state.spaces.byId[id])
    .filter(Boolean) as SpaceOption[];
};

export const selectSpacesStatusForSize = (
  state: RootState,
  sizeId?: number | null
) => {
  if (sizeId == null || isNaN(sizeId)) return "idle" as const;
  return state.spaces.bySize[sizeId]?.status ?? "idle";
};
